import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import type { PlanId } from '@/config/access-control'
import { kbdSubmitProps } from '@/hooks/formKeyboardNav'
import { getConsoleUrl, isConsoleDeployment } from '@/utils/deployment'
import { getPlanDisplayName } from '@/utils/subscription'
import { Link } from '@tanstack/react-router'
import { Lock } from 'lucide-react'
import type { ReactNode } from 'react'

export interface PlanLockedPopoverProps {
	requiredPlan: PlanId
	currentPlan: PlanId | null
	billingHref: string
	label?: string
	children: ReactNode
}

export function PlanLockedPopover({ requiredPlan, billingHref, label, children }: PlanLockedPopoverProps) {
	const planDisplay = getPlanDisplayName(requiredPlan)
	const isConsole = isConsoleDeployment()
	const targetHref = !isConsole && billingHref.startsWith('/') ? `${getConsoleUrl()}${billingHref}` : billingHref

	const isExternal = targetHref.startsWith('http://') || targetHref.startsWith('https://')

	return (
		<Popover>
			<PopoverTrigger asChild>{children}</PopoverTrigger>
			<PopoverContent align='end' className='w-80 z-[300]'>
				<div className='space-y-3'>
					<div className='flex items-start gap-2'>
						<Lock className='size-(--icon-standalone) text-muted-foreground shrink-0' />
						<div className='space-y-1'>
							<p className='text-body font-medium text-foreground'>Available on {planDisplay}</p>
							<p className='text-control text-muted-foreground'>
								{label ?? `Upgrade to ${planDisplay} to unlock this feature.`}
							</p>
						</div>
					</div>
					<Button asChild size='sm' className='w-full' {...kbdSubmitProps()}>
						{isExternal ? (
							<a href={targetHref} target='_blank' rel='noopener noreferrer'>
								Upgrade plan
							</a>
						) : (
							<Link to={targetHref}>Upgrade plan</Link>
						)}
					</Button>
				</div>
			</PopoverContent>
		</Popover>
	)
}
